'use client';

import { c as _c } from "react/compiler-runtime";
import { AssetPath, PROJECT_PATH_PREFIX } from '@gnsx/genesys.js';
import { BUILT_PROJECT_FOLDER } from '@gnsx/genesys.js';
import { CheckIcon, FileTextIcon, FilterIcon, FolderIcon, Loader2Icon, MinusIcon, SearchIcon, UploadIcon, XIcon } from 'lucide-react';
import pLimit from 'p-limit';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Progress } from '../../../components/ui/progress';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import { storageFactory, StorageProviderType } from '../../../lib/storage';
import { computeFileHash } from '../../../lib/storage/hash-utils';
import type { IStorageProvider } from '../../../lib/storage';
import type { AssetType } from '@/editor/core/assetTypes';
import type { LucideIcon } from 'lucide-react';
import type { IconType } from 'react-icons';
import { ASSET_ICONS, getAssetType, getAssetTypeDisplayName } from '@/editor/core/assetTypes';
import { gamesApi } from '@/lib/api/games';
interface GameAssetUploaderProps {
  gameId: string;
  isOpen: boolean;
  onClose: () => void;
  sourceProvider: IStorageProvider;
  onUploadComplete?: () => void;
}
type AssetIconComponent = LucideIcon | IconType;
interface GameAssetListItem {
  assetType: AssetType;
  assetTypeLabel: string;
  file: File;
  fileName: string;
  fileSizeLabel: string;
  IconComponent: AssetIconComponent;
  searchName: string;
}
const IGNORED_NAMES = ['.DS_Store', 'node_modules', '__MACOSX', '.git', '.gitignore', '.env', '.vscode'];

// Configuration for parallel uploads
const MAX_CONCURRENT_UPLOADS = 10; // Number of files to upload simultaneously

// Helper function to determine MIME type from file extension
const getMimeTypeFromFileName = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase() ?? '';
  const mimeTypes: Record<string, string> = {
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'svg': 'image/svg+xml',
    'webp': 'image/webp',
    'mp3': 'audio/mpeg',
    'wav': 'audio/wav',
    'ogg': 'audio/ogg',
    'mp4': 'video/mp4',
    'webm': 'video/webm',
    'json': 'application/json',
    'txt': 'text/plain',
    'html': 'text/html',
    'css': 'text/css',
    'js': 'application/javascript',
    'xml': 'application/xml',
    'pdf': 'application/pdf',
    'zip': 'application/zip',
    'gltf': 'model/gltf+json',
    'glb': 'model/gltf-binary',
    'fbx': 'application/octet-stream',
    'usd': 'model/vnd.usd',
    'usda': 'model/vnd.usda',
    'usdc': 'model/vnd.usdc',
    'usdz': 'model/vnd.usdz+zip',
    'obj': 'text/plain',
    'mtl': 'text/plain'
  };
  return mimeTypes[extension] || 'application/octet-stream';
};
type UploadFileStatus = 'pending' | 'uploading' | 'success' | 'skipped' | 'error';

/** Determine asset type from a browser File object for display purposes */
const getAssetTypeFromFile = (file: File): AssetType => {
  // Adapt browser File to the shape expected by getAssetType
  return getAssetType({
    name: file.name,
    path: file.name,
    isDirectory: false
  } as unknown as Parameters<typeof getAssetType>[0]);
};
export const GameAssetUploader = ({
  gameId,
  isOpen,
  onClose,
  sourceProvider,
  onUploadComplete
}: GameAssetUploaderProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadStatus, setUploadStatus] = useState<Record<string, UploadFileStatus>>({});
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [comparingFiles, setComparingFiles] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);

  // New state for search, selection, and filtering
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [assetTypeFilter, setAssetTypeFilter] = useState<AssetType | 'all'>('all');

  // Stores local SHA-256 hashes keyed by file.name, populated during load.
  const localHashesRef = useRef<Map<string, string>>(new Map());
  const loadRequestIdRef = useRef(0);
  const uploadProgressRef = useRef<Map<string, number>>(new Map());
  const updateFileProgress = useCallback((fileName: string, nextProgress: number) => {
    const roundedProgress = Math.round(nextProgress);
    const previousProgress = uploadProgressRef.current.get(fileName);
    if (previousProgress === roundedProgress) return;
    const shouldSkipUpdate = previousProgress !== undefined && roundedProgress > previousProgress && roundedProgress < 100 && roundedProgress - previousProgress < 5;
    if (shouldSkipUpdate) return;
    uploadProgressRef.current.set(fileName, roundedProgress);
    setUploadProgress(prev => prev[fileName] === roundedProgress ? prev : {
      ...prev,
      [fileName]: roundedProgress
    });
  }, []);
  const fileItems = useMemo(() => {
    return files.map((file): GameAssetListItem => {
      const assetType = getAssetTypeFromFile(file);
      return {
        assetType,
        assetTypeLabel: getAssetTypeDisplayName(assetType),
        file,
        fileName: file.name,
        fileSizeLabel: formatFileSize(file.size),
        IconComponent: ASSET_ICONS[assetType] ?? FileTextIcon,
        searchName: file.name.toLowerCase()
      };
    });
  }, [files]);

  // Compute total upload size for selected pending files
  const totalUploadSize = useMemo(() => {
    let totalBytes = 0;
    for (const item of fileItems) {
      if (uploadStatus[item.fileName] === 'pending' && selectedFiles.has(item.fileName)) {
        totalBytes += item.file.size;
      }
    }
    return totalBytes;
  }, [fileItems, selectedFiles, uploadStatus]);
  const filteredFileItems = useMemo(() => {
    const normalisedSearchQuery = searchQuery.toLowerCase();
    return fileItems.filter(item_0 => {
      const matchesSearch = normalisedSearchQuery === '' || item_0.searchName.includes(normalisedSearchQuery);
      const matchesType = assetTypeFilter === 'all' || item_0.assetType === assetTypeFilter;
      return matchesSearch && matchesType;
    });
  }, [assetTypeFilter, fileItems, searchQuery]);
  const availableAssetTypes = useMemo(() => {
    const types = new Set<AssetType>();
    for (const item_1 of fileItems) {
      types.add(item_1.assetType);
    }
    return Array.from(types).sort();
  }, [fileItems]);
  const fileCounts = useMemo(() => {
    let pendingCount = 0;
    let selectedPendingCount = 0;
    let skippedCount = 0;
    for (const item_2 of fileItems) {
      const status = uploadStatus[item_2.fileName];
      if (status === 'pending') {
        pendingCount += 1;
        if (selectedFiles.has(item_2.fileName)) {
          selectedPendingCount += 1;
        }
      }
      if (status === 'skipped') {
        skippedCount += 1;
      }
    }
    return {
      pendingCount,
      selectedPendingCount,
      skippedCount
    };
  }, [fileItems, selectedFiles, uploadStatus]);
  const filteredCounts = useMemo(() => {
    let filteredPendingCount = 0;
    let filteredSelectedCount = 0;
    for (const item_3 of filteredFileItems) {
      if (uploadStatus[item_3.fileName] === 'pending') {
        filteredPendingCount += 1;
      }
      if (selectedFiles.has(item_3.fileName)) {
        filteredSelectedCount += 1;
      }
    }
    return {
      filteredPendingCount,
      filteredSelectedCount
    };
  }, [filteredFileItems, selectedFiles, uploadStatus]);

  // Toggle file selection
  const toggleFileSelection = useCallback((fileName_0: string) => {
    setSelectedFiles(prev_0 => {
      const newSet = new Set(prev_0);
      if (newSet.has(fileName_0)) {
        newSet.delete(fileName_0);
      } else {
        newSet.add(fileName_0);
      }
      return newSet;
    });
  }, []);

  // Select all visible (filtered) files
  const selectAllVisible = useCallback(() => {
    setSelectedFiles(prev_1 => {
      const newSet_0 = new Set(prev_1);
      for (const item_4 of filteredFileItems) {
        if (uploadStatus[item_4.fileName] === 'pending') {
          newSet_0.add(item_4.fileName);
        }
      }
      return newSet_0;
    });
  }, [filteredFileItems, uploadStatus]);

  // Deselect all visible files
  const deselectAllVisible = useCallback(() => {
    setSelectedFiles(prev_2 => {
      const newSet_1 = new Set(prev_2);
      for (const item_5 of filteredFileItems) {
        newSet_1.delete(item_5.fileName);
      }
      return newSet_1;
    });
  }, [filteredFileItems]);

  // Load files from the source provider's .dist folder, compute local hashes,
  // fetch remote hashes in a single call, and pre-mark unchanged files as skipped.
  const loadFilesFromProvider = useCallback(async () => {
    const currentLoadRequestId = ++loadRequestIdRef.current;
    setLoadingFiles(true);
    setComparingFiles(false);
    try {
      const remoteHashesPromise = (async () => {
        try {
          const currentProvider = storageFactory.getDefaultProvider();
          if (currentProvider.providerType !== StorageProviderType.FIREBASE) {
            return {} as Record<string, string>;
          }
          const response = await gamesApi.getAssetHashes(gameId);
          return response.hashes;
        } catch (error_0) {
          // Non-fatal: if we can't fetch remote hashes, treat every file as changed.
          console.warn('Could not fetch remote hashes — all files will be uploaded:', error_0);
          return {} as Record<string, string>;
        }
      })();
      const distPath = AssetPath.fromString(AssetPath.join(PROJECT_PATH_PREFIX, BUILT_PROJECT_FOLDER));
      const fileList = await sourceProvider.listFiles(distPath, true, true);
      const fileItems_0 = fileList.files.filter(fileItem => !IGNORED_NAMES.some(ignored => fileItem.name.includes(ignored)));
      const limit = pLimit(10);
      const loadedFiles = (await Promise.all(fileItems_0.map(fileItem_0 => limit(async () => {
        try {
          // Download the file content
          const content = await sourceProvider.downloadFile(AssetPath.fromString(fileItem_0.path), {
            asBlob: true
          });

          // Create a File object with the relative path (remove .dist prefix)
          const relativePath = fileItem_0.path.replace(`${PROJECT_PATH_PREFIX}/`, '');
          const contentType = fileItem_0.contentType ?? getMimeTypeFromFileName(fileItem_0.name);
          let file_0: File;
          if (content instanceof Blob) {
            file_0 = new File([content], relativePath, {
              type: contentType
            });
          } else if (typeof content === 'string') {
            file_0 = new File([content], relativePath, {
              type: contentType
            });
          } else {
            // ArrayBuffer
            file_0 = new File([content], relativePath, {
              type: contentType
            });
          }
          return file_0;
        } catch (error_1) {
          console.error(`Failed to load file ${fileItem_0.path}:`, error_1);
          return null;
        }
      })))).filter((file_1): file_1 is File => file_1 !== null);
      if (loadRequestIdRef.current !== currentLoadRequestId) {
        return;
      }
      const initialSelectedFiles = new Set(loadedFiles.map(file_2 => file_2.name));
      const initialUploadStatus = Object.fromEntries(loadedFiles.map(file_3 => [file_3.name, 'pending' as UploadFileStatus]));
      setFiles(loadedFiles);
      setUploadStatus(initialUploadStatus);
      setSelectedFiles(initialSelectedFiles);
      setLoadingFiles(false);
      if (loadedFiles.length === 0) {
        localHashesRef.current = new Map();
        return;
      }
      setComparingFiles(true);
      const [hashResults, remoteHashes] = await Promise.all([Promise.all(loadedFiles.map(async file_4 => {
        try {
          const hash = await computeFileHash(file_4);
          return {
            name: file_4.name,
            hash
          };
        } catch {
          return {
            name: file_4.name,
            hash: null
          };
        }
      })), remoteHashesPromise]);
      if (loadRequestIdRef.current !== currentLoadRequestId) {
        return;
      }
      const newLocalHashes = new Map<string, string>();
      for (const {
        name,
        hash: hash_0
      } of hashResults) {
        if (hash_0) newLocalHashes.set(name, hash_0);
      }
      localHashesRef.current = newLocalHashes;

      // Diff: mark files whose local hash matches the remote hash as skipped.
      const newUploadStatus: Record<string, UploadFileStatus> = {};
      const newSelectedFiles = new Set<string>();
      for (const file_5 of loadedFiles) {
        const localHash = newLocalHashes.get(file_5.name);
        const remoteHash = remoteHashes[file_5.name];
        if (localHash && remoteHash && localHash === remoteHash) {
          newUploadStatus[file_5.name] = 'skipped';
        } else {
          newUploadStatus[file_5.name] = 'pending';
          // Auto-select pending files for upload
          newSelectedFiles.add(file_5.name);
        }
      }
      setUploadStatus(newUploadStatus);
      setSelectedFiles(newSelectedFiles);
    } catch (error) {
      console.error('Failed to load files from provider:', error);
      toast.error('Failed to load files from project');
    } finally {
      if (loadRequestIdRef.current === currentLoadRequestId) {
        setLoadingFiles(false);
        setComparingFiles(false);
      }
    }
  }, [sourceProvider, gameId]);

  // Clear state when dialog is opened
  useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setUploading(false);
      setUploadProgress({});
      setUploadStatus({});
      setLoadingFiles(false);
      setComparingFiles(false);
      setOverallProgress(0);
      setUploadedCount(0);
      setTotalFiles(0);
      setSearchQuery('');
      setSelectedFiles(new Set());
      setAssetTypeFilter('all');
      localHashesRef.current = new Map();
      uploadProgressRef.current = new Map();
      // Load files from the source provider
      void loadFilesFromProvider();
    }
  }, [isOpen, loadFilesFromProvider]);

  // Drag and drop functionality removed - files are now loaded automatically from source provider

  const removeFile = useCallback((fileName_1: string) => {
    setFiles(files_0 => files_0.filter(file_6 => file_6.name !== fileName_1));
    setUploadStatus(prev_3 => {
      const updatedStatus = {
        ...prev_3
      };
      delete updatedStatus[fileName_1];
      return updatedStatus;
    });
    setUploadProgress(prev_4 => {
      const updatedProgress = {
        ...prev_4
      };
      delete updatedProgress[fileName_1];
      return updatedProgress;
    });
    setSelectedFiles(prev_5 => {
      const nextSelectedFiles = new Set(prev_5);
      nextSelectedFiles.delete(fileName_1);
      return nextSelectedFiles;
    });
    localHashesRef.current.delete(fileName_1);
    uploadProgressRef.current.delete(fileName_1);

    // Update total files count
    setTotalFiles(prev_6 => Math.max(0, prev_6 - 1));
  }, []);
  const uploadFile = async (file_7: File, currentTotalFiles: number) => {
    try {
      // Update status to uploading
      uploadProgressRef.current.set(file_7.name, 0);
      setUploadProgress(prev_8 => ({
        ...prev_8,
        [file_7.name]: 0
      }));
      setUploadStatus(prev_9 => ({
        ...prev_9,
        [file_7.name]: 'uploading'
      }));
      console.log('Uploading', file_7);

      // Ensure we have a content type
      const contentType_0 = file_7.type || getMimeTypeFromFileName(file_7.name);

      // Get current storage provider to determine upload method
      const currentProvider_0 = storageFactory.getDefaultProvider();
      const providerType = currentProvider_0.providerType;
      console.log(`Using ${providerType} provider for uploading ${file_7.name}`);
      if (providerType === StorageProviderType.FIREBASE || providerType === StorageProviderType.S3) {
        // Firebase/S3: Use signed URL approach
        console.log(`Uploading to ${providerType} using signed URL`);
        await uploadFileToSignedUrl(file_7, contentType_0);
      } else if (providerType === StorageProviderType.FILE_SERVER) {
        // FileServer: Use storage provider directly
        console.log('Uploading to FileServer using storage provider');
        await uploadFileToFileServer(file_7, contentType_0);
      } else {
        throw new Error(`Upload not supported for provider type: ${providerType}`);
      }
      console.log(`Successfully uploaded ${file_7.name}`);
      updateFileProgress(file_7.name, 100);
      setUploadStatus(prev_10 => ({
        ...prev_10,
        [file_7.name]: 'success'
      }));

      // Update uploaded count and overall progress using the passed totalFiles
      setUploadedCount(prev_11 => {
        const newCount = prev_11 + 1;
        // Use the passed currentTotalFiles instead of state variable
        const percentage = currentTotalFiles > 0 ? Math.round(newCount / currentTotalFiles * 100) : 0;
        setOverallProgress(Math.min(percentage, 100)); // Cap at 100%
        return newCount;
      });
    } catch (error_2) {
      console.error(`Error uploading ${file_7.name}:`, error_2);
      setUploadStatus(prev_7 => ({
        ...prev_7,
        [file_7.name]: 'error'
      }));
      throw error_2;
    }
  };

  // Upload using signed URLs (Firebase or S3), followed by a metadata update to store
  // the content hash. The hash cannot be embedded in the signed URL as an
  // extension header because that triggers a CORS preflight which signed
  // URLs do not support. Instead we write the hash via a separate API call
  // after the PUT completes.
  const uploadFileToSignedUrl = async (file_8: File, contentType_1: string) => {
    try {
      const contentHash = localHashesRef.current.get(file_8.name);
      const response_0 = await gamesApi.generateSignedUrl(gameId, {
        filePath: file_8.name,
        contentType: contentType_1,
        action: 'write'
      });
      const {
        signedUrl
      } = response_0;

      // Upload the file using XMLHttpRequest for progress tracking
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', signedUrl, true);
        xhr.setRequestHeader('Content-Type', contentType_1);
        xhr.upload.onprogress = event => {
          if (event.lengthComputable) {
            const progress = event.loaded / event.total * 100;
            updateFileProgress(file_8.name, progress);
          }
        };
        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve();
          } else {
            reject(new Error(`Failed to upload ${file_8.name}: ${xhr.status}`));
          }
        };
        xhr.onerror = () => {
          reject(new Error(`Network error occurred while uploading ${file_8.name}`));
        };
        xhr.send(file_8);
      });
      if (contentHash) {
        await gamesApi.setAssetContentHash(gameId, {
          filePath: file_8.name,
          contentHash
        });
      }
    } catch (error_3) {
      console.error(`Firebase upload error for ${file_8.name}:`, error_3);
      throw error_3;
    }
  };

  // FileServer upload using storage provider
  const uploadFileToFileServer = async (file_9: File, contentType_2: string) => {
    try {
      const provider = storageFactory.getProvider(StorageProviderType.FILE_SERVER);
      const filePath = AssetPath.fromString(AssetPath.join(`/games/${gameId}`, file_9.name));
      const contentHash_0 = localHashesRef.current.get(file_9.name);

      // Use real progress tracking for FileServer
      await provider.uploadFile(filePath, file_9, {
        contentType: contentType_2,
        additional: {
          gameId
        },
        metadata: contentHash_0 ? {
          contentHash: contentHash_0
        } : undefined,
        onProgress: progress_0 => {
          updateFileProgress(file_9.name, progress_0);
        }
      });
    } catch (error_4) {
      console.error(`FileServer upload error for ${file_9.name}:`, error_4);
      throw error_4;
    }
  };

  // Helper function to process uploads with true concurrency control
  // Maintains exactly MAX_CONCURRENT_UPLOADS active uploads at all times
  const uploadFilesWithConcurrencyControl = async (filesToUpload: File[], totalFilesCount: number): Promise<string[]> => {
    const errors: string[] = [];
    const fileQueue = [...filesToUpload]; // Create a copy to work with
    const activeUploads = new Set<Promise<void>>();

    // Function to start an upload and manage the active uploads pool
    const startNextUpload = (): Promise<void> | null => {
      if (fileQueue.length === 0) return null;
      const file_10 = fileQueue.shift()!;
      const uploadPromise = (async () => {
        try {
          await uploadFile(file_10, totalFilesCount);
        } catch (error_5) {
          errors.push(file_10.name);
        }
      })();

      // Handle completion and start next upload
      const wrappedPromise = uploadPromise.finally(() => {
        // Remove this upload from active set when complete
        activeUploads.delete(wrappedPromise);

        // Start next upload if there are files remaining
        const nextUpload = startNextUpload();
        if (nextUpload) {
          activeUploads.add(nextUpload);
        }
      });
      return wrappedPromise;
    };

    // Start initial batch of uploads up to the concurrency limit
    for (let i = 0; i < Math.min(MAX_CONCURRENT_UPLOADS, filesToUpload.length); i++) {
      const uploadPromise_0 = startNextUpload();
      if (uploadPromise_0) {
        activeUploads.add(uploadPromise_0);
      }
    }

    // Wait for all uploads to complete
    while (activeUploads.size > 0) {
      await Promise.race(activeUploads);
    }
    return errors;
  };
  const uploadAllFiles = async () => {
    if (files.length === 0) return;

    // Only upload files that are pending AND selected
    const filesToUpload_0 = files.filter(file_11 => uploadStatus[file_11.name] === 'pending' && selectedFiles.has(file_11.name));
    const skippedCount_0 = files.filter(file_12 => uploadStatus[file_12.name] === 'skipped').length;
    const alreadyUploaded = files.filter(file_13 => uploadStatus[file_13.name] === 'success').length;
    const unselectedCount = files.filter(file_14 => uploadStatus[file_14.name] === 'pending' && !selectedFiles.has(file_14.name)).length;
    if (filesToUpload_0.length === 0) {
      if (unselectedCount > 0) {
        toast.info(`No files selected for upload — ${unselectedCount} pending file${unselectedCount !== 1 ? 's' : ''} unselected`);
      } else {
        toast.success(skippedCount_0 > 0 ? `All files up to date — ${skippedCount_0} unchanged file${skippedCount_0 !== 1 ? 's' : ''} skipped` : 'All files already uploaded');
      }
      return;
    }
    setUploading(true);
    // Total that counts toward progress: files to upload + already uploaded (not skipped)
    const progressTotal = filesToUpload_0.length + alreadyUploaded;
    setTotalFiles(progressTotal);
    setUploadedCount(alreadyUploaded);
    setOverallProgress(progressTotal > 0 ? Math.round(alreadyUploaded / progressTotal * 100) : 0);
    try {
      const errors = await uploadFilesWithConcurrencyControl(filesToUpload_0, progressTotal);
      if (errors.length === 0) {
        const msg = skippedCount_0 > 0 ? `Upload complete — ${skippedCount_0} unchanged file${skippedCount_0 !== 1 ? 's' : ''} skipped` : 'All files uploaded successfully';
        toast.success(msg);
        onClose();
        onUploadComplete?.();
      } else {
        toast.error(`Failed to upload ${errors.length} file(s). Please try again.`);
      }
    } finally {
      setUploading(false);
    }
  };
  return <TooltipProvider>
      <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
        <DialogContent className="sm:max-w-4xl p-6 h-[70vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <UploadIcon className="h-5 w-5" />
              Upload Game Assets
            </DialogTitle>
          </DialogHeader>

          {loadingFiles ? <div className="flex flex-col items-center justify-center p-10 border-2 border-dashed rounded-md mt-4 mb-2">
              <Loader2Icon className="h-12 w-12 text-muted-foreground mb-4 animate-spin" />
              <p className="text-sm font-medium">Loading files from <span className="bg-primary/10 text-primary px-2 py-1 rounded font-mono font-semibold">{BUILT_PROJECT_FOLDER}</span> folder
              </p>
              <p className="text-xs text-muted-foreground mt-2">Preparing assets for upload
                to {gameId}</p>
            </div> : files.length === 0 ? <div className="mt-4 mb-2 flex flex-col items-center justify-center rounded-md border-2 border-dashed border-warning/60 bg-warning/10 p-10">
              <UploadIcon className="mb-4 h-12 w-12 text-warning" />
              <p className="text-sm font-medium text-warning">
                No files found in <span className="rounded bg-warning/20 px-2 py-1 font-mono font-semibold text-warning">{BUILT_PROJECT_FOLDER}</span> folder
              </p>
              <p className="mt-2 text-xs text-warning/80">Make sure your project has been built</p>
            </div> : <div className="flex flex-col items-center justify-center p-4 border border-border rounded-md mt-4 mb-2 bg-muted/30">
              <div className="flex items-center gap-2 mb-2">
                <FolderIcon className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium text-foreground">
                  {files.length} file{files.length !== 1 ? 's' : ''} found
                </span>
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                {fileCounts.pendingCount > 0 && <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-primary" />
                    {fileCounts.pendingCount} pending
                  </span>}
                {fileCounts.skippedCount > 0 && <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-muted-foreground" />
                    {fileCounts.skippedCount} unchanged
                  </span>}
                {fileCounts.selectedPendingCount > 0 && fileCounts.selectedPendingCount < fileCounts.pendingCount && <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-primary" />
                    {fileCounts.selectedPendingCount} selected for upload
                  </span>}
                {comparingFiles && <span className="flex items-center gap-1">
                    <Loader2Icon className="h-3 w-3 animate-spin" />
                    Checking unchanged files
                  </span>}
              </div>
            </div>}

          {files.length > 0 && <div className="flex flex-col flex-1 min-h-0">
              {/* Toolbar with search and filters */}
              <div className="flex items-center gap-2 mb-3 shrink-0">
                <div className="relative flex-1">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search files..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9" />
                </div>
                <Select value={assetTypeFilter} onValueChange={value => setAssetTypeFilter(value as AssetType | 'all')}>
                  <SelectTrigger className="w-40" size="default">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {availableAssetTypes.map(type => <SelectItem key={type} value={type}>
                        {getAssetTypeDisplayName(type)}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1">
                  <Button variant="default" onClick={selectAllVisible} disabled={filteredCounts.filteredPendingCount === 0}>
                    Select All
                  </Button>
                  <Button variant="default" onClick={deselectAllVisible} disabled={filteredCounts.filteredSelectedCount === 0}>
                    None
                  </Button>
                </div>
              </div>

              {comparingFiles && <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2 mb-3 shrink-0">
                  <span className="text-xs text-muted-foreground">
                    Comparing local file hashes with uploaded assets to skip unchanged files.
                  </span>
                  <Loader2Icon className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>}

              {/* Overall progress during upload */}
              {uploading && <div className="space-y-2 mb-3 shrink-0">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Overall Progress</span>
                    <span className="text-sm text-muted-foreground">
                      {uploadedCount} of {totalFiles} files uploaded ({totalFiles > 0 ? overallProgress : 0}%)
                    </span>
                  </div>
                  <Progress value={totalFiles > 0 ? overallProgress : 0} className="w-full" />
                </div>}

              {/* File list with ScrollArea */}
              <div className="flex-1 min-h-0 border rounded-md">
                <ScrollArea className="h-full w-full">
                  <div className="p-4 space-y-1">
                    {filteredFileItems.length === 0 ? <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                        <FilterIcon className="h-8 w-8 mb-2 opacity-50" />
                        <p className="text-sm">No files match your filters</p>
                        <p className="text-xs mt-1">Try adjusting your search or type filter</p>
                      </div> : filteredFileItems.map(item_6 => {
                  return <GameAssetUploaderFileRow key={item_6.fileName} IconComponent={item_6.IconComponent} assetTypeLabel={item_6.assetTypeLabel} fileName={item_6.fileName} fileSizeLabel={item_6.fileSizeLabel} isSelected={selectedFiles.has(item_6.fileName)} isUploading={uploading} onRemove={removeFile} onToggle={toggleFileSelection} progress={uploadProgress[item_6.fileName] ?? 0} status={uploadStatus[item_6.fileName]} />;
                })}
                  </div>
                </ScrollArea>
              </div>

              {/* Footer stats */}
              <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground shrink-0">
                <span>
                  Showing {filteredFileItems.length} of {files.length} files
                  {searchQuery && ` (filtered by "${searchQuery}")`}
                  {assetTypeFilter !== 'all' && ` • ${getAssetTypeDisplayName(assetTypeFilter)} only`}
                </span>
                <span>
                  {filteredCounts.filteredSelectedCount} selected • {filteredCounts.filteredPendingCount} pending upload
                  {totalUploadSize > 0 && ` • ${formatFileSize(totalUploadSize)} total`}
                </span>
              </div>
            </div>}

          <DialogFooter className="shrink-0 mt-4">
            <Button variant="default" onClick={onClose} disabled={uploading}>
              Cancel
            </Button>
            <Button variant="primaryHover" onClick={uploadAllFiles} disabled={files.length === 0 || uploading || loadingFiles || comparingFiles || fileCounts.selectedPendingCount === 0}>
              {uploading ? <>
                  <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                  Uploading {uploadedCount}/{totalFiles}...
                </> : comparingFiles ? <>
                  <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                  Checking files...
                </> : <>
                  <UploadIcon className="mr-2 h-4 w-4" />
                  {fileCounts.selectedPendingCount > 0 ? `Upload ${fileCounts.selectedPendingCount} file${fileCounts.selectedPendingCount !== 1 ? 's' : ''}${fileCounts.skippedCount > 0 ? ` (${fileCounts.skippedCount} unchanged)` : ''}` : fileCounts.pendingCount > 0 ? 'Select files to upload' : 'All up to date'}
                </>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>;
};
GameAssetUploader.displayName = 'GameAssetUploader';
function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
function getFileStatusIcon(status: UploadFileStatus | undefined) {
  switch (status) {
    case 'uploading':
      return <Loader2Icon className="h-4 w-4 animate-spin" />;
    case 'success':
      return <CheckIcon className="h-4 w-4 text-success" />;
    case 'skipped':
      return <MinusIcon className="h-4 w-4 text-muted-foreground" />;
    case 'error':
      return <XIcon className="h-4 w-4 text-destructive" />;
    default:
      return null;
  }
}
interface GameAssetUploaderFileRowProps {
  assetTypeLabel: string;
  fileName: string;
  fileSizeLabel: string;
  IconComponent: AssetIconComponent;
  isSelected: boolean;
  isUploading: boolean;
  onRemove: (fileName: string) => void;
  onToggle: (fileName: string) => void;
  progress: number;
  status: UploadFileStatus | undefined;
}
const GameAssetUploaderFileRow = React.memo(t0 => {
  const $ = _c(63);
  const {
    assetTypeLabel,
    fileName,
    fileSizeLabel,
    IconComponent,
    isSelected,
    isUploading,
    onRemove,
    onToggle,
    progress,
    status
  } = t0;
  const rowClassName = `flex items-center justify-between rounded-md border p-2 transition-all [content-visibility:auto] [contain-intrinsic-size:44px] ${status === "pending" ? isSelected ? "border-primary/25 bg-primary/10 hover:bg-primary/20 cursor-pointer" : "border-border bg-input-idle opacity-60 hover:bg-input-hover cursor-pointer" : status === "success" ? "border-green-200 bg-green-50/50" : status === "skipped" ? "border-border bg-muted/30 opacity-50" : status === "error" ? "border-destructive/30 bg-destructive/10" : "border-border bg-input-idle hover:bg-input-hover"}`;
  let t1;
  if ($[0] !== fileName || $[1] !== onToggle || $[2] !== status) {
    t1 = () => status === "pending" && onToggle(fileName);
    $[0] = fileName;
    $[1] = onToggle;
    $[2] = status;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== isSelected || $[5] !== status) {
    t2 = status === "pending" ? <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-[4px] outline outline-1 ${isSelected ? "bg-primary text-primary-foreground outline-primary" : "bg-background/40 text-muted-foreground outline-border"}`}>{isSelected ? <CheckIcon className="h-3.5 w-3.5 stroke-3" /> : null}</span> : <span className="w-5 shrink-0">{getFileStatusIcon(status)}</span>;
    $[4] = isSelected;
    $[5] = status;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = `h-4 w-4 shrink-0 ${status === "success" ? "text-success" : status === "error" ? "text-destructive" : "text-muted-foreground"}`;
  let t4;
  if ($[7] !== IconComponent || $[8] !== t3) {
    t4 = <IconComponent className={t3} />;
    $[7] = IconComponent;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = `min-w-0 truncate text-sm font-medium ${isSelected ? "text-foreground" : "text-muted-foreground"}`;
  let t6;
  if ($[10] !== fileName || $[11] !== t5) {
    t6 = <span className={t5}>{fileName}</span>;
    $[10] = fileName;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t2 || $[14] !== t4 || $[15] !== t6) {
    t7 = <div className="flex min-w-0 flex-1 items-center gap-3">{t2}{t4}{t6}</div>;
    $[13] = t2;
    $[14] = t4;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = `text-0.5xs font-medium ${isSelected ? "text-primary" : "text-muted-foreground"}`;
  let t9;
  if ($[17] !== assetTypeLabel || $[18] !== t8) {
    t9 = <span className={t8}>{assetTypeLabel}</span>;
    $[17] = assetTypeLabel;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const t10 = `hidden whitespace-nowrap text-xs font-medium sm:block ${isSelected ? "text-foreground" : "text-muted-foreground"}`;
  let t11;
  if ($[20] !== fileSizeLabel || $[21] !== t10) {
    t11 = <span className={t10}>{fileSizeLabel}</span>;
    $[20] = fileSizeLabel;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== progress || $[24] !== status) {
    t12 = status === "uploading" && <div className="flex items-center gap-2"><Progress value={progress} className="h-2 w-16" /><span className="w-10 text-right text-xs text-muted-foreground">{progress}%</span></div>;
    $[23] = progress;
    $[24] = status;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== status) {
    t13 = status === "success" && <span className="text-xs font-medium text-success">Done</span>;
    $[26] = status;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== status) {
    t14 = status === "skipped" && <span className="text-xs font-medium text-muted-foreground">Unchanged</span>;
    $[28] = status;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== status) {
    t15 = status === "error" && <span className="text-xs font-medium text-destructive">Failed</span>;
    $[30] = status;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== fileName || $[33] !== onRemove) {
    t16 = e => {
      e.stopPropagation();
      onRemove(fileName);
    };
    $[32] = fileName;
    $[33] = onRemove;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const t17 = isUploading && status === "uploading";
  let t18;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <XIcon className="h-3 w-3" />;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t16 || $[37] !== t17) {
    t19 = <Button variant="ghost" size="icon" className="h-6 w-6" onClick={t16} disabled={t17}>{t18}</Button>;
    $[36] = t16;
    $[37] = t17;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t11 || $[40] !== t12 || $[41] !== t13 || $[42] !== t14 || $[43] !== t15 || $[44] !== t19 || $[45] !== t9) {
    t20 = <div className="ml-2 flex shrink-0 items-center gap-3">{t9}{t11}{t12}{t13}{t14}{t15}{t19}</div>;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
    $[44] = t19;
    $[45] = t9;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== rowClassName || $[48] !== t1 || $[49] !== t20 || $[50] !== t7) {
    t21 = <TooltipTrigger asChild={true}><div className={rowClassName} onClick={t1}>{t7}{t20}</div></TooltipTrigger>;
    $[47] = rowClassName;
    $[48] = t1;
    $[49] = t20;
    $[50] = t7;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== fileName) {
    t22 = <p className="font-normal">{fileName}</p>;
    $[52] = fileName;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== assetTypeLabel || $[55] !== fileSizeLabel) {
    t23 = <p className="mt-1 text-xs text-muted-foreground">{assetTypeLabel} • {fileSizeLabel}</p>;
    $[54] = assetTypeLabel;
    $[55] = fileSizeLabel;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== t22 || $[58] !== t23) {
    t24 = <TooltipContent side="bottom" className="max-w-md">{t22}{t23}</TooltipContent>;
    $[57] = t22;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t21 || $[61] !== t24) {
    t25 = <Tooltip>{t21}{t24}</Tooltip>;
    $[60] = t21;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  return t25;
});
GameAssetUploaderFileRow.displayName = 'GameAssetUploaderFileRow';